import { useMemo, useState, type ReactElement, type SyntheticEvent } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi, getFieldViolations } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type { ProvisionUserInput } from '../api';

interface FieldErrors {
  email?: string;
  name?: string;
  fullName?: string;
  roles?: string;
}

/** Server field name → local form field. Used to map back bean-validation violations. */
const SERVER_FIELD_MAP: Readonly<Record<string, keyof FieldErrors>> = {
  email: 'email',
  name: 'name',
  fullName: 'fullName',
  roles: 'roles',
};

interface DraftState {
  email: string;
  name: string;
  fullName: string;
  roles: string[];
}

const EMPTY_DRAFT: DraftState = {
  email: '',
  name: '',
  fullName: '',
  roles: [],
};

/**
 * Provision-user form (PTV2-1021 / PTV2-1426). Roles are picked from the tenant's own role catalogue
 * ({@code roleOptions}, sourced from {@code useTenantRoles}) via a dropdown that adds chips — no more
 * free-text typing, so an operator can only assign roles that actually exist in the tenant. Mirrors the
 * catalogue-driven picker in {@code AssignTenantUserRolesDialog}.
 */
export function ProvisionUserForm({
  roleOptions,
  rolesLoading = false,
  onSubmit,
  pending,
  submitError,
}: {
  /** The tenant's role catalogue (role names). */
  readonly roleOptions: readonly string[];
  /** Whether the role catalogue is still loading. */
  readonly rolesLoading?: boolean;
  readonly onSubmit: (input: Omit<ProvisionUserInput, 'tenantUuid'>) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});

  // Catalogue roles not already picked — the selectable options.
  const availableRoles = useMemo(
    () => roleOptions.filter((role) => !draft.roles.includes(role)),
    [roleOptions, draft.roles],
  );

  function addRole(role: string): void {
    const trimmed = role.trim();
    if (trimmed.length === 0) return;
    setDraft((p) => (p.roles.includes(trimmed) ? p : { ...p, roles: [...p.roles, trimmed] }));
  }

  function removeRole(role: string): void {
    setDraft((p) => ({ ...p, roles: p.roles.filter((r) => r !== role) }));
  }

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    const next: FieldErrors = {};
    const email = draft.email.trim();
    const name = draft.name.trim();
    const fullName = draft.fullName.trim();
    if (email.length === 0) next.email = t('routes.platform.detail.users.errors.emailRequired');
    else if (!/.+@.+\..+/.test(email)) {
      next.email = t('routes.platform.detail.users.errors.emailInvalid');
    }
    if (name.length === 0) next.name = t('routes.platform.detail.users.errors.nameRequired');
    if (draft.roles.length === 0) {
      next.roles = t('routes.platform.detail.users.errors.rolesRequired');
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    // Omit fullName when blank (server treats it as optional); the mutation
    // does the conditional spread, but we also drop the empty key here.
    onSubmit({
      email,
      name,
      roles: draft.roles,
      ...(fullName.length > 0 ? { fullName } : {}),
    });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  // Map server-side bean-validation violations back to the originating fields
  // so the operator sees them inline (the messages are Hibernate-generated and
  // safe to render — see FieldViolation in api/errors).
  const serverFieldErrors = useMemo<FieldErrors>(() => {
    const out: FieldErrors = {};
    for (const violation of getFieldViolations(submitError)) {
      const local = SERVER_FIELD_MAP[violation.field];
      if (local !== undefined && out[local] === undefined) {
        out[local] = violation.message;
      }
    }
    return out;
  }, [submitError]);

  // Client-side errors take precedence; fall back to server field errors.
  const fieldError = (field: keyof FieldErrors): string | undefined =>
    errors[field] ?? serverFieldErrors[field];

  const noCatalogue = !rolesLoading && roleOptions.length === 0;

  return (
    <form onSubmit={handleSubmit} noValidate className="max-w-xl space-y-4">
      <p className="text-sm text-muted-foreground">{t('routes.platform.detail.users.note')}</p>
      <FormField
        id="provision-email"
        label={t('routes.platform.detail.users.fields.email')}
        required
        error={fieldError('email')}
      >
        {(fp) => (
          <Input
            {...fp}
            type="email"
            value={draft.email}
            onChange={(e) => { setDraft((p) => ({ ...p, email: e.target.value })); }}
            autoComplete="off"
          />
        )}
      </FormField>
      <FormField
        id="provision-name"
        label={t('routes.platform.detail.users.fields.name')}
        required
        error={fieldError('name')}
      >
        {(fp) => (
          <Input
            {...fp}
            value={draft.name}
            onChange={(e) => { setDraft((p) => ({ ...p, name: e.target.value })); }}
            autoComplete="off"
          />
        )}
      </FormField>
      <FormField
        id="provision-fullname"
        label={t('routes.platform.detail.users.fields.fullName')}
        error={fieldError('fullName')}
      >
        {(fp) => (
          <Input
            {...fp}
            value={draft.fullName}
            onChange={(e) => { setDraft((p) => ({ ...p, fullName: e.target.value })); }}
            autoComplete="off"
          />
        )}
      </FormField>
      <FormField
        id="provision-roles"
        label={t('routes.platform.detail.users.fields.roles')}
        required
        error={fieldError('roles')}
      >
        {(fp) => (
          <div>
            <Select
              {...fp}
              value=""
              disabled={rolesLoading || noCatalogue || availableRoles.length === 0}
              onChange={(e) => {
                addRole(e.target.value);
              }}
            >
              <option value="" disabled>
                {rolesLoading
                  ? t('routes.platform.detail.users.rolesLoading')
                  : noCatalogue
                    ? t('routes.platform.detail.users.rolesNoCatalog')
                    : availableRoles.length === 0
                      ? t('routes.platform.detail.users.rolesAllAdded')
                      : t('routes.platform.detail.users.rolesPlaceholder')}
              </option>
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Select>
            {draft.roles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {draft.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
                  >
                    {role}
                    <button
                      type="button"
                      onClick={() => { removeRole(role); }}
                      aria-label={t('routes.platform.detail.users.removeRole', { role })}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              {t('routes.platform.detail.users.rolesHint')}
            </p>
          </div>
        )}
      </FormField>
      {submitError !== undefined && submitError !== null && (
        <p role="alert" className="text-sm text-red-600">
          {t('routes.platform.detail.users.errors.submit', { code: submitErrorCode })}
        </p>
      )}
      <Button type="submit" loading={pending}>
        {t('routes.platform.detail.users.submit')}
      </Button>
    </form>
  );
}
